import React, { useState } from 'react';

/**
 * Sources editor for claim entries.
 * Claim sources use: sourceName, sourceLink, archiveLinks[], videoPreviewLink, videoLink, hasBeenDeleted
 * (Different structure from library SourcesEditor which uses name/url)
 */
export default function ClaimSourcesEditor({ sources = [], onSave, onClose }) {
  const [items, setItems] = useState(sources.map(s => ({
    sourceName: s.sourceName || '',
    sourceLink: s.sourceLink || '',
    archiveLinks: s.archiveLinks || [],
    videoPreviewLink: s.videoPreviewLink || '',
    videoLink: s.videoLink || '',
    hasBeenDeleted: s.hasBeenDeleted || false,
  })));

  const addItem = () => setItems([...items, {
    sourceName: '', sourceLink: '', archiveLinks: [],
    videoPreviewLink: '', videoLink: '', hasBeenDeleted: false,
  }]);

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  // Archive link management
  const addArchiveLink = (idx) => {
    const updated = [...items];
    updated[idx] = {
      ...updated[idx],
      archiveLinks: [...updated[idx].archiveLinks, { service: '', url: '' }],
    };
    setItems(updated);
  };

  const updateArchiveLink = (sourceIdx, archiveIdx, field, value) => {
    const updated = [...items];
    const archives = [...updated[sourceIdx].archiveLinks];
    archives[archiveIdx] = { ...archives[archiveIdx], [field]: value };
    updated[sourceIdx] = { ...updated[sourceIdx], archiveLinks: archives };
    setItems(updated);
  };

  const removeArchiveLink = (sourceIdx, archiveIdx) => {
    const updated = [...items];
    updated[sourceIdx] = {
      ...updated[sourceIdx],
      archiveLinks: updated[sourceIdx].archiveLinks.filter((_, i) => i !== archiveIdx),
    };
    setItems(updated);
  };

  const handleSave = () => {
    const cleaned = items
      .filter(s => s.sourceName.trim() || s.sourceLink.trim())
      .map(s => {
        const out = {
          sourceName: s.sourceName,
          sourceLink: s.sourceLink,
          archiveLinks: s.archiveLinks.filter(a => a.url.trim()),
        };
        if (s.videoPreviewLink) out.videoPreviewLink = s.videoPreviewLink;
        if (s.videoLink) out.videoLink = s.videoLink;
        if (s.hasBeenDeleted) out.hasBeenDeleted = true;
        return out;
      });
    onSave(cleaned);
    onClose();
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>Edit Claim Sources</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {items.map((item, idx) => (
            <div key={idx} style={{ border: '1px solid #333', borderRadius: '6px', padding: '12px', marginBottom: '12px', background: '#1a1a1a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.8rem', color: '#888' }}>Source {idx + 1}</span>
                <button className="admin-btn-remove" onClick={() => removeItem(idx)} title="Remove source">×</button>
              </div>

              <label className="admin-label">Source name (supports HTML)</label>
              <input
                className="admin-input"
                value={item.sourceName}
                onChange={e => updateItem(idx, 'sourceName', e.target.value)}
                placeholder="e.g. <b>BBC News</b> reports that..."
              />

              <label className="admin-label">Source link</label>
              <input
                className="admin-input"
                value={item.sourceLink}
                onChange={e => updateItem(idx, 'sourceLink', e.target.value)}
                placeholder="https://..."
              />

              {/* Archive links */}
              <label className="admin-label">Archive links</label>
              {item.archiveLinks.map((al, aIdx) => (
                <div key={aIdx} className="admin-source-row" style={{ marginBottom: '4px' }}>
                  <input
                    className="admin-input"
                    value={al.service}
                    onChange={e => updateArchiveLink(idx, aIdx, 'service', e.target.value)}
                    placeholder="Service (e.g. Archive.org)"
                    style={{ flex: '0 0 30%' }}
                  />
                  <input
                    className="admin-input"
                    value={al.url}
                    onChange={e => updateArchiveLink(idx, aIdx, 'url', e.target.value)}
                    placeholder="https://web.archive.org/..."
                  />
                  <button className="admin-btn-remove" onClick={() => removeArchiveLink(idx, aIdx)}>×</button>
                </div>
              ))}
              <button type="button" className="admin-btn-add" onClick={() => addArchiveLink(idx)} style={{ fontSize: '0.8rem' }}>
                + Archive link
              </button>

              {/* Optional fields */}
              <div className="admin-row" style={{ marginTop: '8px' }}>
                <div className="admin-field">
                  <label className="admin-label">Video preview link</label>
                  <input
                    className="admin-input"
                    value={item.videoPreviewLink}
                    onChange={e => updateItem(idx, 'videoPreviewLink', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="admin-field">
                  <label className="admin-label">Video link</label>
                  <input
                    className="admin-input"
                    value={item.videoLink}
                    onChange={e => updateItem(idx, 'videoLink', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '0.85rem', color: '#ccc', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={item.hasBeenDeleted}
                  onChange={e => updateItem(idx, 'hasBeenDeleted', e.target.checked)}
                />
                Source has been deleted
              </label>
            </div>
          ))}
          <button type="button" className="admin-btn-add" onClick={addItem}>+ Add source</button>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="admin-btn-primary" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}
